/**
 * This plugin is loosely based on Tiny's fullpage plugin 5.10.4
 * - elements not relevant to our use case have been removed: toolbar button & context menu
 * - updated to TypeScript
 * - Firefox incompatibility issue has been resolved
 * - configuration settings removed as irrelevant to our use case: fullpage_hide_in_source_view,
 *   fullpage_default_xml_pi, fullpage_default_font_family, fullpage_default_font_size,
 *   fullpage_default_text_color, fullpage_default_title
 *
 * IMPORTANT
 * The Full Page plugin (fullpage) was deprecated in TinyMCE ver 5.9 and removed in ver 6.0.
 * https://www.tiny.cloud/docs/plugins/opensource/fullpage/
 * https://github.com/tinymce/tinymce-dist/tree/5.8.2/plugins/fullpage
 *
 * Date: 2022-10-19
 */

type Editor = {
  dom: any;
  getBody: () => void;
  getDoc: () => any;
  getParam: (param: string, defaultVal?: string) => string | boolean;
  on: (evtName: string, cb: (evt: any) => void) => void;
  schema: any;
};

type Cell = {
  get: () => any;
  set: (param: string) => void;
};

const iterableFullpagePlugin = (tinyEditor: Editor) => {
  const cell = (initial: any): Cell => {
    let value = initial;
    const get = () => {
      return value;
    };
    const set = (v: any) => {
      value = v;
    };
    return {
      get,
      set,
    };
  };

  const tinyTools = window.tinymce.util.Tools.resolve('tinymce.util.Tools');

  const tinyDomParser = window.tinymce.util.Tools.resolve('tinymce.html.DomParser');

  const getDefaultEncoding = (editor: Editor) => {
    return editor.getParam('fullpage_default_encoding');
  };
  const getDefaultDocType = (editor: Editor) => {
    return editor.getParam('fullpage_default_doctype', '<!DOCTYPE html>');
  };
  const getProtect = (editor: Editor) => {
    return editor.getParam('protect');
  };

  const parseHeader = (editor: Editor, head: any) => {
    // IMPORTANT: FireFox will throw an error "XML Parsing Error: no root element found" if
    // format: 'xhtml' (which will lead to other errors and a missing <body> in the editor)
    return tinyDomParser(
      {
        validate: false,
        root_name: '#document',
      },
      editor.schema,
    ).parse(head, { format: 'html' });
  };

  const protectHtml = (protect: any, html: string) => {
    tinyTools.each(protect, (pattern: string) => {
      html = html.replace(pattern, (str: string) => {
        return `<!--mce:protected ${escape(str)}-->`;
      });
    });
    return html;
  };

  const unprotectHtml = (html: string) => {
    return html.replace(/<!--mce:protected ([\s\S]*?)-->/g, (a, m) => {
      return unescape(m);
    });
  };

  const low = (s: string) => {
    return s.replace(/<\/?[A-Z]+/g, (a: string) => {
      return a.toLowerCase();
    });
  };

  // Create a header if none exists in the source html
  const getDefaultHeader = (editor: Editor) => {
    let header = '';
    const contentEncoding = getDefaultEncoding(editor);
    header += getDefaultDocType(editor);
    header += '\n<html>\n<head>\n';
    if (contentEncoding) {
      header += `<meta http-equiv="Content-Type" content="text/html; charset=${contentEncoding}" />\n`;
    }
    header += `</head>\n<body>\n`;
    return header;
  };

  const handleSetContent = (editor: Editor, headState: Cell, footState: Cell, evt: any) => {
    let startPos;
    let endPos;
    let content;
    let styles = '';
    const { dom } = editor;

    if (evt.selection) {
      return;
    }
    content = protectHtml(getProtect(editor), evt.content);
    if (evt.format === 'raw' && headState.get()) {
      return;
    }
    if (content.length === 0 && !evt.source_view) {
      content = `${tinyTools.trim(headState.get())}\n${tinyTools.trim(content)}\n${tinyTools.trim(
        footState.get(),
      )}`;
    }
    content = content.replace(/<(\/?)BODY/gi, '<$1body');
    startPos = content.indexOf('<body');
    if (startPos !== -1) {
      startPos = content.indexOf('>', startPos);
      headState.set(low(content.substring(0, startPos + 1)));
      endPos = content.indexOf('</body', startPos);
      if (endPos === -1) {
        endPos = content.length;
      }

      evt.content = tinyTools.trim(content.substring(startPos + 1, endPos));
      footState.set(low(content.substring(endPos)));
    } else {
      headState.set(getDefaultHeader(editor));
      footState.set('\n</body>\n</html>');
    }

    const headerFragment = parseHeader(editor, headState.get());
    tinyTools.each(headerFragment.getAll('style'), (node: any) => {
      if (node.firstChild) {
        styles += node.firstChild.value;
      }
    });

    const bodyElm = headerFragment.getAll('body')[0];
    if (bodyElm) {
      dom.setAttribs(editor.getBody(), {
        style: bodyElm.attr('style') || '',
        dir: bodyElm.attr('dir') || '',
        vLink: bodyElm.attr('vlink') || '',
        link: bodyElm.attr('link') || '',
        aLink: bodyElm.attr('alink') || '',
      });
    }
    dom.remove('fullpage_styles');
    const headElm = editor.getDoc().getElementsByTagName('head')[0];
    if (styles) {
      const styleElm = dom.add(headElm, 'style', { id: 'fullpage_styles' });
      styleElm.appendChild(document.createTextNode(styles));
    }
    const currentStyleSheetsMap: Record<string, any> = {};
    tinyTools.each(headElm.getElementsByTagName('link'), (stylesheet: any) => {
      if (stylesheet.rel === 'stylesheet' && stylesheet.getAttribute('data-mce-fullpage')) {
        currentStyleSheetsMap[stylesheet.href] = stylesheet;
      }
    });
    tinyTools.each(headerFragment.getAll('link'), (stylesheet: any) => {
      const href = stylesheet.attr('href');
      if (!href) {
        return true;
      }
      if (!currentStyleSheetsMap[href] && stylesheet.attr('rel') === 'stylesheet') {
        dom.add(headElm, 'link', {
          rel: 'stylesheet',
          text: 'text/css',
          href,
          'data-mce-fullpage': '1',
        });
      }
      delete currentStyleSheetsMap[href];
    });
    tinyTools.each(currentStyleSheetsMap, (stylesheet: any) => {
      stylesheet.parentNode.removeChild(stylesheet);
    });
  };

  const handleGetContent = (head: any, foot: any, evt: any) => {
    if (evt.format === 'html' && !evt.selection) {
      evt.content = unprotectHtml(
        `${tinyTools.trim(head)}\n${tinyTools.trim(evt.content)}\n${tinyTools.trim(foot)}`,
      );
    }
  };

  const setup = (editor: Editor, headState: Cell, footState: Cell) => {
    editor.on('beforesetcontent', (evt: any) => {
      handleSetContent(editor, headState, footState, evt);
    });
    editor.on('getcontent', (evt: any) => {
      handleGetContent(headState.get(), footState.get(), evt);
    });
  };

  const tinyHeadState = cell('');
  const tinyFootState = cell('');
  setup(tinyEditor, tinyHeadState, tinyFootState);
};

export default iterableFullpagePlugin;
